import {
  Box,
  Grid,
  Paper,
  Theme,
  Typography,
} from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import { useRoutes } from '@roc/client-portal-shared/hooks';
import { GlobalStore, Layout } from '@roc/feature-app-core';
import { usePlatformLoanRoutes } from '@roc/feature-loans-routes-config';
import { Button, TestDataAttribute } from '@roc/ui';
import { observer } from 'mobx-react';
import { Link, useHistory } from 'react-router-dom';
import { IQuoteTypeStore } from '../../stores/common/iQuoteTypeStore';
import { useThirdPartyLoanRoutes } from '../../hooks/useThirdPartyLoanRoutes';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    title: {
      fontSize: '34px',
    },
    paper: {
      overflow: 'hidden',
    },
    stepperWrapper: {
      flex: 1,
      padding: theme.spacing(3.5, 3.5),
      [theme.breakpoints.down('xs')]: {
        padding: theme.spacing(1, 1),
      },
    },
    fieldSpacing: {
      padding: theme.spacing(2),
    },
    rowSpacing: {
      marginTop: theme.spacing(4),
      marginBottom: theme.spacing(2),
    },
    centerText: {
      textAlign: 'center',
    },
    spacedTypography: {
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(2),
    },
    attention: {
      fontWeight: 'bold',
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(2),
    },
    link: {
      color: theme.palette.primary.dark,
      fontWeight: 'bold',
    },
    contactLine: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
    },
    contactIcon: {
      marginRight: '8px',
      fontSize: '1.40rem',
    },
    largeButton: {
      border: '2px solid #F2BC16',
      cursor: 'pointer',
      minHeight: '160px',
      '&:hover': {
        backgroundColor: '#fdefbf',
        transition: theme.transitions.create(
          ['background-color', 'transform'],
          { duration: theme.transitions.duration.standard }
        ),
      },
    },
    buttonTitle: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      marginBottom: '16px',
    },
    buttonContent: {
      padding: '8px',
    },
    buttonIcon: {
      backgroundColor: '#F2BC16',
      color: '#fff',
      fontSize: '48px',
    },
    buttonContainer: {
      padding: '5px',
      backgroundColor: '#F2BC16',
      borderRadius: '6px',
      height: '60px',
      width: '60px',
    },
    buttonTitleText: {
      marginLeft: '20px',
    },
    buttonsSection: {
      marginBottom: '32px',
    },
    loanIdSection: {
      marginTop: '32px',
      marginBottom: '40px',
    },
    loanIdTitle: {
      fontWeight: 'bold',
    },
    loanGrid: {
      marginLeft: '20px',
    },
    loanDivider: {
      marginTop: '4px',
      marginBottom: '6px',
    },
  })
);

export interface LoanSuccessProps {
  loanNumber: string;
  globalStore: GlobalStore;
}

export const LoanSuccessStep = observer(
  ({ store }: { store: IQuoteTypeStore }) => {
    const classes = useStyles();
    const isEditingLoan = Boolean(store.savedQuote?.loanNumber);
    return (
      <Layout maxWidth={'lg'}>
        <Paper className={classes.paper} elevation={0}>
          <Box display="flex">
            <Box className={classes.stepperWrapper}>
              {isEditingLoan ? (
                <EditLoanSuccess loanNumber={store.savedLoan.loanNumber} globalStore={store.globalStore} />
              ) : (
                <CreateLoanSuccess loanNumber={store.savedLoan.loanNumber} globalStore={store.globalStore} />
              )}
            </Box>
          </Box>
        </Paper>
      </Layout>
    );
  }
);

export function CreateLoanSuccess(props: LoanSuccessProps) {
  const { loanNumber, globalStore } = props;
  const classes = useStyles();

  return (
    <Grid container justifyContent="space-around">
      <Grid item xs={12}>
        <Box className={classes.centerText}>
          <Typography variant="h5">Loan Created Successfully!</Typography>
        </Box>
        <Box className={classes.centerText} pb={2}>
          <Typography className={classes.spacedTypography} variant="body1">
            Your loan has been saved and assigned{' '}
            <TestDataAttribute id="loanIdLink">
              <LoanLink loanNumber={loanNumber} className={classes.link} globalStore={globalStore} />
            </TestDataAttribute>
            .
          </Typography>
          <Typography variant="body1">
            You can now add supporting information and documents, or return home
            and continue later.
          </Typography>
        </Box>
      </Grid>
      <Grid container item xs={12} direction="row" justifyContent="center" spacing={2}>
        <Grid item xs={10} md={3}>
          <ReturnHomeButton globalStore={globalStore} />
        </Grid>
        <Grid item xs={10} md={3}>
          <CompleteTodosButton loanNumber={loanNumber} globalStore={globalStore} />
        </Grid>
      </Grid>
    </Grid>
  );
}

export function EditLoanSuccess(props: LoanSuccessProps) {
  const { loanNumber, globalStore } = props;
  const classes = useStyles();

  return (
    <Grid container justifyContent="space-around">
      <Grid item xs={12}>
        <Box className={classes.centerText}>
          <Typography variant="h5">Loan Saved Successfully!</Typography>
        </Box>
        <Box className={classes.centerText} pb={2}>
          <Typography className={classes.spacedTypography} variant="body1">
            Your loan has been saved{' '}
            <TestDataAttribute id="loanIdLink">
              <LoanLink loanNumber={loanNumber} className={classes.link} globalStore={globalStore} />
            </TestDataAttribute>
            .
          </Typography>
          <Typography variant="body1">
            You can now add supporting information and documents, or return home
            and continue later.
          </Typography>
        </Box>
      </Grid>
      <Grid container item xs={12} direction="row" justifyContent="center" spacing={2}>
        <Grid item xs={10} md={3}>
          <ReturnHomeButton globalStore={globalStore} />
        </Grid>
        <Grid item xs={10} md={3}>
          <CompleteTodosButton loanNumber={loanNumber} globalStore={globalStore} />
        </Grid>
      </Grid>
    </Grid>
  );
}

function LoanLink({ loanNumber, className, globalStore }: { loanNumber: string; className?: string; globalStore: GlobalStore }) {
  const { platformLoanRoutesConfig } = usePlatformLoanRoutes();
  const { thirdPartyLoanRoutesConfig } = useThirdPartyLoanRoutes();

  if (globalStore.isExternalCapitalApp) {
    const viewPlatformLoanSummaryURL = thirdPartyLoanRoutesConfig.viewPlatformLoanSummary(loanNumber).url;
    return (
      <Link
        className={className}
        to="#"
        onClick={(e) => {
          e.preventDefault();
          window.top.location.href = viewPlatformLoanSummaryURL;
        }}
      >
        loan {loanNumber}
      </Link>
    );
  }

  return (
    <Link className={className} to={platformLoanRoutesConfig.loans(loanNumber).children.dashboard.url}>
      loan {loanNumber}
    </Link>
  );
}

function ReturnHomeButton({ globalStore }: { globalStore: GlobalStore }) {
  const { dashboardRoutesConfig } = useRoutes();
  const { thirdPartyLoanRoutesConfig } = useThirdPartyLoanRoutes();
  const history = useHistory();

  const handleClick = () => {
    if (globalStore.isExternalCapitalApp) {
      window.top.location.href = thirdPartyLoanRoutesConfig.viewLoans.url;
    } else {
      history.push(dashboardRoutesConfig.dashboard.url);
    }
  };

  return (
    <Button
      variant="outlined"
      color="secondary"
      fullWidth
      onClick={handleClick}
      testId="returnToHomeButton"
    >
      RETURN TO HOME
    </Button>
  );
}

function CompleteTodosButton({ loanNumber, globalStore }: { loanNumber: string; globalStore: GlobalStore }) {
  const { platformLoanRoutesConfig } = usePlatformLoanRoutes();
  const { thirdPartyLoanRoutesConfig } = useThirdPartyLoanRoutes();
  const history = useHistory();

  const handleClick = () => {
    if (globalStore.isExternalCapitalApp) {
      window.top.location.href = thirdPartyLoanRoutesConfig.viewPlatformLoanSummary(loanNumber).url;
    } else {
      history.push(platformLoanRoutesConfig.loans(loanNumber).children.tasks.url);
    }
  };

  return (
    <Button
      variant="contained"
      color="secondary"
      fullWidth
      onClick={handleClick}
      testId="completeTodosButton"
    >
      COMPLETE TO-DOS
    </Button>
  );
}
